/**
 * EventEmitter - A simple event system that allows subscribing to and emitting events
 * @class
 */
export default class EventEmitter {
	/** @type {Map} - Private map of event names to arrays of listener objects */
	#events;

	/**
	 * Creates a new EventEmitter instance
	 */
	constructor() {
		this.#events = new Map();
	}

	/**
	 * Binds a listener to an event.
	 * @param {string} event - The event to bind the listener to.
	 * @param {Function} listener - The listener function to bind.
	 * @param {Object} [options] - Optional settings.
	 * @param {boolean} [options.defer=false] - If true, listener runs async (next tick).
	 * @returns {EventEmitter} The current instance for chaining.
	 * @throws {TypeError} If the listener is not a function.
	 */
	on(event, listener, options) {
		if (typeof listener !== 'function') {
			throw new TypeError('Listener must be a function');
		}

		const listeners = this.#events.get(event) || [];
		// Check if this exact listener is already registered
		const exists = listeners.some((entry) => entry.fn === listener);
		if (!exists) {
			listeners.push({ fn: listener, defer: options?.defer || false });
		}
		this.#events.set(event, listeners);

		return this;
	}

	/**
	 * Unbinds a listener from an event.
	 * @param {string} event - The event to unbind the listener from.
	 * @param {Function} listener - The listener function to unbind.
	 * @returns {EventEmitter} The current instance for chaining.
	 */
	off(event, listener) {
		const listeners = this.#events.get(event);
		if (!listeners) return this;

		const index = listeners.findIndex((entry) => entry.fn === listener);
		if (index !== -1) {
			listeners.splice(index, 1);
			if (listeners.length === 0) {
				this.#events.delete(event);
			} else {
				this.#events.set(event, listeners);
			}
		}

		return this;
	}

	/**
	 * Triggers an event and calls all bound listeners.
	 * @param {string} event - The event to trigger.
	 * @param {...*} args - Arguments to pass to the listener functions.
	 * @returns {boolean} True if the event had listeners, false otherwise.
	 */
	emit(event, ...args) {
		const listeners = this.#events.get(event);
		if (!listeners || listeners.length === 0) return false;

		// Copy array before iteration to prevent issues if listeners call off() during emit
		const snapshot = [...listeners];

		for (let i = 0, n = snapshot.length; i < n; ++i) {
			const entry = snapshot[i];
			if (entry === undefined) continue;

			if (entry.defer) {
				// Deferred listeners run on next tick
				setTimeout(() => {
					try {
						entry.fn.apply(this, args);
					} catch (error) {
						console.error(`Error in deferred listener for event '${event}':`, error);
					}
				}, 0);
			} else {
				// Sync listeners run immediately
				try {
					entry.fn.apply(this, args);
				} catch (error) {
					console.error(`Error in listener for event '${event}':`, error);
				}
			}
		}

		return true;
	}

	destroy() {
		this.#events.clear();
	}
}

